import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	styled,
	Typography,
} from "@mui/material";
import { types } from "api";
import { StyledButton } from "components/StyledButton";
import { useAuthStatus } from "helpers/useAuthStatus";
import { ADD_ADMIN, REMOVE_ADMIN } from "messages";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getPath, routes } from "routes";

const PREFIX = "ProfilePageDetails";

const classes = {
	Btn: `${PREFIX}-Btn`,
};

const Root = styled(Grid)(({ theme }) => ({
	width: "100%",

	[`& .${classes.Btn}`]: {
		padding: "10px 25px",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		float: "left",
	},
}));

export interface ProfilePageDetailsProps {
	user: types.User;
	isMe: boolean;
	toggleIsAdmin: (isUserAdmin: boolean) => void;
}

export const ProfilePageDetails = ({
	user,
	isMe,
	toggleIsAdmin,
}: ProfilePageDetailsProps): JSX.Element => {
	const { role } = useAuthStatus();
	const [isDialogVisible, setIsDialogVisible] = useState(false);

	const hideDialog = (): void => setIsDialogVisible(false);

	const showDialog = (): void => setIsDialogVisible(true);

	const isUserAdmin = useMemo(() => user?.role.includes("admin"), [user]);

	const isUserAdminOrTeacher = useMemo(
		() =>
			user?.role.find(
				(r) =>
					r === "admin" || r === "teacher" || r === "admin-teacher",
			) !== undefined,
		[user],
	);

	const isParent = useMemo(() => user?.role.includes("parent"), [user]);

	const toggleIsAdminHandler = (): void => {
		hideDialog();
		toggleIsAdmin(isUserAdmin);
	};

	return (
		<Root>
			<Grid container justifyContent="space-between">
				{/* User details */}
				<Grid>
					<Grid container alignItems="center">
						<Typography variant="h2">
							{`${user.firstName} ${user.lastName}`}
						</Typography>
					</Grid>

					<Grid container alignItems="center">
						<Typography variant="h5">{user.email}</Typography>
					</Grid>

					<Grid container alignItems="flex-start" justifyContent="flex-start">
						{user.role.map((item) => (
							<Chip
								color="secondary"
								key={item}
								label={item}
								sx={{ marginRight: 1 }}
							/>
						))}
					</Grid>
				</Grid>

				{/* Buttons to change permissions and password */}
				<Grid>
					{role === "admin" && !isMe && isUserAdminOrTeacher && (
						<StyledButton
							variant="contained"
							color="secondary"
							className={classes.Btn}
							onClick={showDialog}
						>
							{isUserAdmin
								? "Remove admin permissions"
								: "Add admin permissions"}
						</StyledButton>
					)}

					{(isMe || role === "admin") && (
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.Btn}
							component={Link}
							to={
								isMe
									? routes._.people._.me._.changePassword.path
									: getPath(routes._.people._.userProfile._.resetPassword, {
											userId: user.userId,
									  })
							}
						>
							Change password
						</StyledButton>
					)}

					{isMe && (
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.Btn}
							component={Link}
							to={
								isMe
									? routes._.people._.me._.changeEmail.path
									: getPath(routes._.people._.userProfile._.changeEmail, {
											userId: user.userId,
									  })
							}
						>
							Change email
						</StyledButton>
					)}

					{isMe && (
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.Btn}
							component={Link}
							to={
								isMe
									? routes._.people._.me._.changeName.path
									: getPath(routes._.people._.userProfile._.changeName, {
											userId: user.userId,
									  })
							}
						>
							Change Name
						</StyledButton>
					)}
					{isParent && (
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.Btn}
							component={Link}
							to={
								isMe
									? routes._.people._.me._.updateChild.path
									: getPath(routes._.people._.userProfile._.updateChild, {
											userId: user.userId,
									  })
							}
						>
							Update child
						</StyledButton>
					)}
				</Grid>
			</Grid>

			{/* Dialog to confirm that you really do want to change permission level */}
			<Dialog
				onClose={hideDialog}
				aria-labelledby="customized-dialog-title"
				open={isDialogVisible}
			>
				<DialogTitle>
					{isUserAdmin ? "Remove admin permission" : "Add admin permission"}
				</DialogTitle>
				<DialogContent>
					<Grid container direction="column">
						<Typography variant="subtitle1">
							{isUserAdmin ? REMOVE_ADMIN : ADD_ADMIN}
						</Typography>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button data-testid="cancel-button" onClick={hideDialog}>
						Cancel
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={toggleIsAdminHandler}
						size="small"
					>
						{isUserAdmin ? "Remove" : "Add"}
					</Button>
				</DialogActions>
			</Dialog>
		</Root>
	);
};
